import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import InputMask from 'react-input-mask';
import api from '../../api';
/* actions */
import { editSuppliers } from '../../redux/actions/suppliers';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
/* form-validation */
import schema from '../../form-validation/supplier';
/* utils */
import handleChangeDefault from '../../utils/handleChangeDefault';

function Edit (props) {
  const { id } = props.match.params;
  const INITIAL_VALUES = {
    _id: id || '',
    type: '2',
    name: '',
    cpf: '',
    cnpj: '',
    additionalInformation: '',
    socialName: '',
    company: ''
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, setError } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const suppliers = useSelector(state => state.suppliers);
  const [data, setData] = useState(INITIAL_VALUES);

  useEffect(() => {
    (async function () {
      try {
        const params = { _id: id };
        const { data } = await api().get('/api/suppliers', { params });
        if (data[0]) {
          const newData = {
            ...INITIAL_VALUES,
            ...data[0]
          };
          setData(newData);
        }
      } catch (err) {
        console.error('Error: ', err);
      }
    })();
  }, []);

  const submit = () => {
    dispatch(editSuppliers(data, submitButton.current));
  };

  const handleChangeCPF = evt => {
    const { name, value } = evt.target;
    setData({ ...data, [name]: value });
    if (/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value)) {
      setError(name);
    }
  };

  const handleChangeCNPJ = evt => {
    const { name, value } = evt.target;
    setData({ ...data, [name]: value });
    if (/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(value)) {
      setError(name);
    }
  };

  return (
    <div className="container-fluid">
      {suppliers.editSuccess && (
        <AlertMsg type="success" msg="Fornecedor atualizado com sucesso" />
      )}
      {suppliers.errorContent &&
        !suppliers.errorContent.message.includes('400') &&
        suppliers.editError && (
        <AlertMsg
          type="danger"
          msg="Falha durante atualização do fornecedor"
        />
      )}
      {suppliers.errorContent &&
        suppliers.errorContent.message.includes('400') &&
        suppliers.editError && (
        <AlertMsg
          type="danger"
          msg="Um fornecedor com o CPF/CNPJ informado já está cadastrada"
        />
      )}
      {!suppliers.editSuccess && !suppliers.editError && (
        <AlertMsg type="info" msg="Área atualização de fornecedores" />
      )}
      <FormHeader title="Atualizar Fornecedor" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-1">
            <div className="form-group">
              <label htmlFor="type">
                Tipo<span className="field-required">*</span>
              </label>
              <select
                id="type"
                name="type"
                className="form-control"
                value={data.type}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              >
                <option value="1">Física</option>
                <option value="2">Jurídica</option>
              </select>
              {errors.type && errors.type.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-5">
            <div className="form-group">
              <label htmlFor="name">
                {parseInt(data.type) === 1 ? 'Nome Completo' : 'Razão Social'}
                <span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                autoComplete="off"
                value={data.name}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.name && errors.name.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="socialName">
                Nome {parseInt(data.type) === 1 ? 'Social' : 'Fantasia'}
                <span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="socialName"
                name="socialName"
                autoComplete="off"
                value={data.socialName}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.socialName && errors.socialName.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              {parseInt(data.type) === 1 ? (
                <React.Fragment>
                  <label htmlFor="cpf">
                    CPF<span className="field-required">*</span>
                  </label>
                  <InputMask
                    id="cpf"
                    name="cpf"
                    mask="999.999.999-99"
                    className="form-control"
                    autoComplete="off"
                    placeholder="___.___.___-__"
                    value={data.cpf}
                    onChange={handleChangeCPF}
                    ref={register}
                  />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <label htmlFor="cnpj">
                    CNPJ<span className="field-required">*</span>
                  </label>
                  <InputMask
                    id="cnpj"
                    name="cnpj"
                    mask="99.999.999/9999-99"
                    className="form-control"
                    autoComplete="off"
                    placeholder="__.___.___/____-__"
                    value={data.cnpj}
                    onChange={handleChangeCNPJ}
                    ref={register}
                  />
                </React.Fragment>
              )}
              {errors.cpf && errors.cpf.type === 'cpf' && (
                <ErrorMsgField type="cpf" />
              )}
              {errors.cnpj && errors.cnpj.type === 'cnpj' && (
                <ErrorMsgField type="cnpj" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="additionalInformation">
            Informações Adicionais<span className="field-required">*</span>
          </label>
          <textarea
            className="form-control"
            id="additionalInformation"
            name="additionalInformation"
            rows="3"
            value={data.additionalInformation}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          ></textarea>
          {errors.additionalInformation &&
            errors.additionalInformation.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <FormFooter url="/suppliers" reference={submitButton} />
      </form>
    </div>
  );
}

Edit.propTypes = {
  match: PropTypes.object
};

export default Edit;
